import React, { PropsWithChildren } from 'react'

import { Typography } from '@/components/typography'
import { Theme } from '@/styles/themes'

export type TextProps = PropsWithChildren<{
  /**
   * Size of the text
   *
   * @example
   * ```tsx
   * <Text size="b1" />
   * ```
   *
   */
  size: keyof Theme['fontSizes']

  /**
   * Color of the text
   *
   * @example
   * ```tsx
   * <Text color="primary" />
   * ```
   *
   */
  color?: keyof Theme['colors']

  /**
   * The family of the font
   *
   * @example
   * ```tsx
   * <Text fontFamily="secondary" />
   * ```
   *
   */
  fontFamily?: keyof Theme['fontFamily']

  /**
   * The class name of the text
   *
   * @example
   * ```tsx
   * <Text className="my-class" />
   * ```
   *
   */
  className?: string

  /**
   * The alignment of the text
   *
   * @default 'left'
   * @example
   * ```tsx
   * <Text align="left" />
   * ```
   *
   */
  align?: 'left' | 'center' | 'right'
  lineHeight?: keyof Theme['lineHeight']

  /**
   * Function to be called when the text is clicked
   *
   * @example
   * ```tsx
   * <Text onClick={() => console.log('Text clicked')} />
   * ```
   *
   */
  onClick?: () => void
}>

export const DesignSystemMapper: {
  [key in keyof Theme['fontSizes']]: {
    weight: keyof Theme['fontWeights']
    fontFamily?: keyof Theme['fontFamily']
    lineHeight?: keyof Theme['lineHeight']
  }
} = {
  b1: {
    weight: 'regular',
    lineHeight: 'b1',
  },
  b2: {
    weight: 'regular',
    lineHeight: 'b2',
  },
  b3: {
    weight: 'light',
    lineHeight: 'b3',
  },
  b4: {
    weight: 'light',
    lineHeight: 'b4',
  },
  h1: {
    weight: 'bold',
  },
  h2: {
    weight: 'bold',
    lineHeight: 'h2',
  },
  h3: {
    weight: 'bold',
    lineHeight: 'h3',
  },
  h4: {
    weight: 'bold',
    lineHeight: 'h4',
  },
  h5: {
    weight: 'bold',
    lineHeight: 'h5',
  },
  quality: {
    weight: 'light',
    fontFamily: 'secondary',
  },
}

export const Text: React.FC<TextProps> = ({
  size,
  color,
  fontFamily,
  lineHeight,
  className,
  onClick,
  align,
  children,
}) => {
  const current = DesignSystemMapper[size]

  return (
    <Typography
      size={size}
      weight={current.weight}
      family={fontFamily || current.fontFamily}
      lineHeight={lineHeight || current.lineHeight}
      color={color}
      className={className}
      onClick={onClick}
      align={align}
    >
      {children}
    </Typography>
  )
}
